import { Box, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { PopperContent } from '@/components/poppers/PopperContent';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_SIDE = 4096;
const MAX_IMAGE_PIXELS = 16_000_000;

export async function validateDiagramReferenceImage(file: File) {
  if (!IMAGE_TYPES.includes(file.type))
    throw new Error('Choose one PNG, JPEG, or WebP image.');
  if (file.size > MAX_IMAGE_BYTES)
    throw new Error('Choose an image no larger than 5 MiB.');
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('This image could not be read.'));
      image.src = url;
    });
    if (
      !image.naturalWidth ||
      !image.naturalHeight ||
      image.naturalWidth > MAX_IMAGE_SIDE ||
      image.naturalHeight > MAX_IMAGE_SIDE ||
      image.naturalWidth * image.naturalHeight > MAX_IMAGE_PIXELS
    )
      throw new Error(
        'Choose an image up to 4096 pixels per side and 16 million pixels total.'
      );
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Keeps the reference local until this turn is sent. */
export function DiagramReferenceImage({
  file,
  onChange,
  onErrorChange,
  onValidatingChange,
  disabled = false,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  onErrorChange: (error: string) => void;
  onValidatingChange?: (validating: boolean) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const selection = useRef(0);
  const [preview, setPreview] = useState<string>();
  const [validating, setValidating] = useState(false);
  useEffect(() => {
    if (!file) {
      setPreview(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(
    () => () => {
      selection.current += 1;
    },
    []
  );
  return (
    <Stack gap={1} minWidth={0}>
      <input
        ref={input}
        hidden
        disabled={disabled || validating}
        type="file"
        aria-label="Diagram reference image"
        accept={IMAGE_TYPES.join(',')}
        onChange={async (event) => {
          const files = Array.from(event.target.files ?? []);
          const next = files[0];
          event.target.value = '';
          if (!next) return;
          const id = ++selection.current;
          setValidating(true);
          onValidatingChange?.(true);
          onErrorChange('');
          try {
            if (files.length !== 1) throw new Error('Choose one image.');
            await validateDiagramReferenceImage(next);
            if (selection.current === id) onChange(next);
          } catch (error) {
            if (selection.current === id)
              onErrorChange(
                error instanceof Error
                  ? error.message
                  : 'This image could not be read.'
              );
          } finally {
            if (selection.current === id) {
              setValidating(false);
              onValidatingChange?.(false);
            }
          }
        }}
      />
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        {file && (
          <>
            {preview && (
              <Box
                component="img"
                src={preview}
                alt="Layout reference preview"
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            )}
            <Button
              size="xs"
              variant="transparent"
              disabled={disabled || validating}
              onClick={() => {
                selection.current += 1;
                onChange(null);
                onErrorChange('');
              }}
            >
              Remove reference
            </Button>
          </>
        )}
        <Button
          size="xs"
          variant="secondary"
          startIcon={Upload01Icon}
          iconProps={{ 'aria-hidden': true, size: 16 }}
          disabled={disabled || validating}
          contextualHelp={
            <PopperContent body="Upload an image of an existing diagram to use as a layout reference for a new diagram. Choose a PNG, JPEG, or WebP image up to 5 MiB." />
          }
          onClick={() => input.current?.click()}
        >
          Upload reference image
        </Button>
      </Stack>
    </Stack>
  );
}
